import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import useMeta from "@/hooks/useMeta";
import TaskModal from "@/components/TaskModal";
import { Card, Spinner, EmptyState, Avatar, StatusBadge, WeightBadge, TimelineBadge, Select, usePagination, Pagination } from "@/components/UI";
import { TASK_TYPE_META } from "@/lib/constants";
import { ArrowLeft, ListOrdered, CheckCircle2, AlertTriangle } from "lucide-react";

const SPRINT_STATUS_LABEL = { planning: "Planning", active: "Active", completed: "Completed" };
const SPRINT_STATUS_CLS = {
  planning: "bg-sky-500/15 text-sky-400",
  active: "bg-emerald-500/15 text-emerald-400",
  completed: "bg-slate-500/15 text-slate-400",
};

function KetepatanBadge({ task }) {
  if (!task.deadline) return <span className="text-slate-600">—</span>;
  if (task.timeline_status === "late") {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-rose-500/15 px-2 py-0.5 text-[11px] font-semibold text-rose-400">
        <AlertTriangle size={11} /> Terlambat
      </span>
    );
  }
  if (task.status === "done") {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-400">
        <CheckCircle2 size={11} /> Tepat Waktu
      </span>
    );
  }
  return <span className="rounded-md bg-slate-500/15 px-2 py-0.5 text-[11px] font-semibold text-slate-400">On Track</span>;
}

function TypeBadge({ type }) {
  const meta = TASK_TYPE_META[type] || TASK_TYPE_META.project;
  return (
    <span
      className="rounded-md px-2 py-0.5 text-[11px] font-semibold"
      style={{ color: meta.color, backgroundColor: `${meta.color}15` }}
    >
      {meta.label}
    </span>
  );
}

export default function SprintDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const meta = useMeta();
  const [data, setData] = useState(null);
  const [tasks, setTasks] = useState(null);
  const [modal, setModal] = useState({ open: false, mode: "view", task: null });
  const [filterProject, setFilterProject] = useState("");
  const [filterPic, setFilterPic] = useState("");

  useEffect(() => {
    api.get(`/sprints/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Gagal memuat sprint"));
    api.get("/tasks", { params: { sprint_id: id } }).then((r) => setTasks(r.data)).catch(() => toast.error("Gagal memuat task"));
  }, [id]);

  const sprint = data?.sprint;
  const baseTasks = (tasks || []).filter((t) => !["cancelled", "replaced"].includes(t.status));
  const visible = baseTasks.filter((t) => {
    if (filterProject && String(t.project_id) !== filterProject) return false;
    if (filterPic && String(t.assignee_id) !== filterPic) return false;
    return true;
  });
  const pg = usePagination(visible, 15);

  if (!data || !tasks) return <Spinner />;

  // Opsi filter diambil dari task yang ada di sprint ini
  const projectOptions = [...new Map(baseTasks.filter((t) => t.project).map((t) => [t.project_id, t.project.name])).entries()];
  const picOptions = [...new Map(baseTasks.filter((t) => t.assignee).map((t) => [t.assignee_id, t.assignee.name])).entries()];
  const doneWeight = visible.filter((t) => t.status === "done").reduce((s, t) => s + (Number(t.weight) || 0), 0);
  const totalWeight = visible.reduce((s, t) => s + (Number(t.weight) || 0), 0);
  const lateCount = visible.filter((t) => t.timeline_status === "late").length;
  const perf = totalWeight > 0 ? Math.round((doneWeight / totalWeight) * 100) : null;

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="sprint-detail-page">
      <div>
        <button
          onClick={() => navigate("/sprints")}
          className="mb-2 flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300"
          data-testid="sprint-detail-back"
        >
          <ArrowLeft size={14} /> Kembali ke List Sprint
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="font-heading text-3xl font-bold tracking-tight">{sprint?.name}</h1>
          <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${SPRINT_STATUS_CLS[sprint?.status] || "bg-slate-500/15 text-slate-400"}`}>
            {SPRINT_STATUS_LABEL[sprint?.status] || sprint?.status}
          </span>
        </div>
        <p className="mt-1 text-sm text-slate-400">
          {sprint?.start_date ? new Date(sprint.start_date).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" }) : "—"}
          {" — "}
          {sprint?.end_date ? new Date(sprint.end_date).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" }) : "—"}
        </p>
      </div>

      {/* Ringkasan */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card className="p-3">
          <p className="text-[11px] uppercase tracking-wider text-slate-500">Total Task</p>
          <p className="font-mono text-xl font-semibold text-slate-100">{visible.length}</p>
        </Card>
        <Card className="p-3">
          <p className="text-[11px] uppercase tracking-wider text-slate-500">Bobot Selesai</p>
          <p className="font-mono text-xl font-semibold text-slate-100">{doneWeight}/{totalWeight} pts</p>
        </Card>
        <Card className="p-3">
          <p className="text-[11px] uppercase tracking-wider text-slate-500">Performance</p>
          <p className="font-mono text-xl font-semibold text-slate-100">{perf == null ? "—" : `${perf}%`}</p>
        </Card>
        <Card className="p-3">
          <p className="text-[11px] uppercase tracking-wider text-rose-400">Terlambat</p>
          <p className="font-mono text-xl font-semibold text-slate-100">{lateCount} task</p>
        </Card>
      </div>

      {/* Filter task */}
      <div className="flex flex-wrap items-center gap-2.5">
        <Select className="w-full sm:w-52" value={filterProject} onChange={(e) => setFilterProject(e.target.value)} data-testid="sprint-detail-filter-project">
          <option value="">Semua Project</option>
          {projectOptions.map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </Select>
        <Select className="w-full sm:w-52" value={filterPic} onChange={(e) => setFilterPic(e.target.value)} data-testid="sprint-detail-filter-pic">
          <option value="">Semua PIC</option>
          {picOptions.map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </Select>
      </div>

      {/* Tabel riwayat task */}
      {visible.length === 0 ? (
        <EmptyState
          icon={ListOrdered}
          title="Tidak ada task"
          subtitle={baseTasks.length === 0 ? "Sprint ini tidak memiliki task." : "Tidak ada task yang cocok dengan filter."}
        />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-800 bg-slate-950/60 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Task</th>
                <th className="px-4 py-3">Jenis</th>
                <th className="px-4 py-3">Project / Tahapan</th>
                <th className="px-4 py-3">PIC</th>
                <th className="px-4 py-3">Bobot</th>
                <th className="px-4 py-3">Deadline</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Ketepatan</th>
              </tr>
            </thead>
            <tbody>
              {pg.paged.map((t) => (
                <tr
                  key={t.id}
                  onClick={() => setModal({ open: true, mode: "view", task: t })}
                  className="cursor-pointer border-b border-slate-800/60 last:border-0 hover:bg-white/[0.02]"
                  data-testid={`sprint-task-row-${t.id}`}
                >
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-200">{t.title}</p>
                    {t.description && <p className="max-w-xs truncate text-xs text-slate-500">{t.description}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <TypeBadge type={t.type || (t.project_id ? "project" : "internal")} />
                  </td>
                  <td className="px-4 py-3 text-slate-400">
                    {t.project?.name || "—"}
                    {t.phase?.name && <span className="text-slate-600"> · {t.phase.name}</span>}
                  </td>
                  <td className="px-4 py-3">
                    {t.assignee ? (
                      <span className="inline-flex items-center gap-1.5 text-slate-300">
                        <Avatar src={t.assignee.avatar_url} name={t.assignee.name} size={20} />
                        {t.assignee.name}
                      </span>
                    ) : (
                      <span className="text-slate-600">Belum ditugaskan</span>
                    )}
                  </td>
                  <td className="px-4 py-3"><WeightBadge weight={t.weight} /></td>
                  <td className="px-4 py-3">{t.deadline ? <TimelineBadge date={t.deadline} /> : <span className="text-slate-600">—</span>}</td>
                  <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                  <td className="px-4 py-3"><KetepatanBadge task={t} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={pg.page} totalPages={pg.totalPages} total={pg.total} perPage={pg.perPage} onChange={pg.setPage} />
        </Card>
      )}

      <TaskModal
        open={modal.open}
        mode={modal.mode}
        task={modal.task}
        meta={meta}
        onClose={() => setModal({ open: false, mode: "view", task: null })}
        onSaved={() => {}}
      />
    </div>
  );
}
